import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Package, 
  MapPin, 
  BarChart3, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  User,
  Users,
  Truck,
  Plus,
  Upload,
  Search,
  FileText,
  UserCircle,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  CreditCard,
  RotateCcw,
  Receipt
} from 'lucide-react';

const Sidebar = ({ user, onLogout }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const location = useLocation();

  const menuItems = [
    { path: '/', icon: Home, label: 'Dashboard' },
    { path: '/new-order', icon: Plus, label: 'New Order' },
    { path: '/upload-orders', icon: Upload, label: 'Upload Orders' },
    { path: '/deliveries', icon: Package, label: 'Deliveries' },
    { 
      path: '/tracking', 
      icon: MapPin, 
      label: 'Tracking',
      hasSubmenu: true,
      submenu: [
        { path: '/tracking', icon: Search, label: 'Track Orders' },
        { path: '/track-by-customer', icon: User, label: 'Track by Customer' },
        { path: '/track-by-bill', icon: FileText, label: 'Track by Bill No' }
      ]
    },
    { path: '/my-customers', icon: Users, label: 'My Customers' },
    { 
      path: '/reports', 
      icon: BarChart3, 
      label: 'Reports',
      hasSubmenu: true,
      submenu: [
        { path: '/order-reports', icon: FileText, label: 'Order Reports' },
        { path: '/customer-report', icon: User, label: 'Customer Report' },
        { path: '/payment-report', icon: CreditCard, label: 'Payment Report' },
        { path: '/return-report', icon: RotateCcw, label: 'Return Report' },
        { path: '/vat-report', icon: Receipt, label: 'VAT Report' }
      ]
    },
    // { path: '/analytics', icon: TrendingUp, label: 'Analytics' }, // Hidden for now
    { path: '/settings', icon: UserCircle, label: 'Profile' },
  ];

  const isActive = (path) => location.pathname === path;
  const isSubmenuActive = (submenu) => submenu.some(item => isActive(item.path));

  const toggleSubmenu = (path) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  return (
    <>
      {/* Mobile overlay */}
      {!isCollapsed && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-white to-primary-50 border-r border-primary-200
        transform transition-transform duration-300 ease-in-out
        ${isCollapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="gradient-header p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center p-1">
                  <img 
                    src="/images/logo.png" 
                    alt="Delivery Portal Logo" 
                    className="w-full h-full object-contain brightness-0 invert"
                  />
                </div>
                <div>
                  <h1 className="text-white font-bold text-lg">Delivery Portal</h1>
                  <p className="text-primary-100 text-sm">Client Dashboard</p>
                </div>
              </div>
              <button
                onClick={() => setIsCollapsed(true)}
                className="lg:hidden text-white hover:text-primary-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* User Profile */}
          <div className="p-6 border-b border-primary-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.Name  || 'User Name'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.EmailId  || ''}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.MobileNo1  || ''}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isExpanded = expandedMenus[item.path];
              const hasActiveSubmenu = item.hasSubmenu && isSubmenuActive(item.submenu);
              
              if (item.hasSubmenu) {
                return (
                  <div key={item.path}>
                    <button
                      onClick={() => toggleSubmenu(item.path)}
                      className={`
                        w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                        ${hasActiveSubmenu
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-primary-100 hover:to-primary-200 hover:text-primary-700'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-6 mt-2 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`
                                flex items-center space-x-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                ${isActive(subItem.path)
                                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                                  : 'text-gray-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:text-primary-700'
                                }
                              `}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span>{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                    ${isActive(item.path)
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-primary-100 hover:to-primary-200 hover:text-primary-700'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-primary-200">
            <button
              onClick={onLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:text-red-700 transition-all duration-200"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <button
        onClick={() => setIsCollapsed(false)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg shadow-lg"
      >
        <Menu className="w-5 h-5" />
      </button>
    </>
  );
};

export default Sidebar; 